import { Merge, SimplifyDeep } from 'type-fest'

import { DipsSchemas } from '~/clients/dips-client'
import { DipsUnScheduledSurgeriesApiVersion } from '~/hooks/useFlag'
import { isNotNullish } from '~/utils/guards'
import { assertUnreachable } from '~/utils/utils'

import { ToArray } from '../utils'

export type V3_DIPS_Entity = NonNullable<DipsSchemas['SurgeryWaitingListResult']['surgeryWaitingList']>[number]
export type V6_DIPS_Entity = NonNullable<DipsSchemas['SurgeryWaitingListResultV6']['surgeryWaitingList']>[number]
export type V7_DIPS_Entity = NonNullable<DipsSchemas['SurgeryWaitingListResultV7']['surgeryWaitingList']>[number]

type V6ExtendedFields = {
    // V6 renames surgeryBookingId to bookingId
    bookingId?: V6_DIPS_Entity['bookingId']
    // V6 adds these new fields (omitting criticalInformation, as we want to get that from the /critical-information endpoint)
    referral?: V6_DIPS_Entity['referral']
    urgency?: V6_DIPS_Entity['urgency']
}

type V7ExtendedFields = {
    // V7 replaces criticalInformation with hasCriticalInformation
    hasCriticalInformation?: V7_DIPS_Entity['hasCriticalInformation']
}

export type UnifiedUnScheduledSurgeryWithOptionalBookingId = Merge<
    { entity_type: 'dips_un_scheduled_surgery' },
    Omit<V3_DIPS_Entity, 'surgeryBookingId'> & V6ExtendedFields & V7ExtendedFields
>
export type UnifiedUnScheduledSurgery = SimplifyDeep<{ bookingId: string } & Omit<UnifiedUnScheduledSurgeryWithOptionalBookingId, 'bookingId'>>

function isUnifiedUnScheduledSurgeryWithBookingId(entity: UnifiedUnScheduledSurgeryWithOptionalBookingId): entity is UnifiedUnScheduledSurgery {
    return typeof entity.bookingId === 'string'
}

function v3ToUnified(entity: V3_DIPS_Entity): UnifiedUnScheduledSurgeryWithOptionalBookingId {
    const newEntity = {
        ...entity,
        entity_type: 'dips_un_scheduled_surgery',
        bookingId: entity.surgeryBookingId,
    } satisfies UnifiedUnScheduledSurgeryWithOptionalBookingId

    // Remove V3 specific fields that are renamed
    delete newEntity.surgeryBookingId

    return newEntity
}

// Makes V6 backwards compatible with V3, while preserving new V6 fields
function v6ToUnified(entity: V6_DIPS_Entity): UnifiedUnScheduledSurgeryWithOptionalBookingId {
    // Remove V6 specific fields that are renamed or omitted
    const { resources, patient, criticalInformation: _criticalInformation, ...entityRest } = entity
    const { id, nationalIdentityNumber, ...patientRest } = patient

    const newEntity = {
        ...entityRest,
        entity_type: 'dips_un_scheduled_surgery',
        patient: {
            ...patientRest,
            personProxyGuid: id,
            nationalId: nationalIdentityNumber,
        },
        surgeryResources: resources,
        // V6 specific fields
        referral: entity.referral,
        urgency: entity.urgency,
    } satisfies UnifiedUnScheduledSurgeryWithOptionalBookingId

    return newEntity
}

function v7ToUnified(entity: V7_DIPS_Entity): UnifiedUnScheduledSurgeryWithOptionalBookingId {
    // Remove V7 specific fields that are renamed
    const { resources, patient, ...entityRest } = entity
    const { id, nationalIdentityNumber, ...patientRest } = patient

    const newEntity = {
        ...entityRest,
        entity_type: 'dips_un_scheduled_surgery',
        patient: {
            ...patientRest,
            personProxyGuid: id,
            nationalId: nationalIdentityNumber,
        },
        surgeryResources: resources,
        referral: entity.referral,
        urgency: entity.urgency,
        // V7 specific fields
        hasCriticalInformation: entity.hasCriticalInformation,
    } satisfies UnifiedUnScheduledSurgeryWithOptionalBookingId

    return newEntity
}

// This is a map to ensure that if new versions are added to the env, the type system will force us to update the transformer
type IncomingEntity = {
    v3: V3_DIPS_Entity
    v6: V6_DIPS_Entity
    v7: V7_DIPS_Entity
}[DipsUnScheduledSurgeriesApiVersion]

function isEntityV3(entity: IncomingEntity): entity is V3_DIPS_Entity {
    return 'surgeryBookingId' in entity && 'surgeryResources' in entity
}

function isEntityV6(entity: IncomingEntity): entity is V6_DIPS_Entity {
    return 'bookingId' in entity && 'resources' in entity && 'criticalInformation' in entity
}

function isEntityV7(entity: IncomingEntity): entity is V7_DIPS_Entity {
    return 'bookingId' in entity && 'resources' in entity && !('criticalInformation' in entity)
}

function transform(entity: IncomingEntity): UnifiedUnScheduledSurgeryWithOptionalBookingId {
    if (isEntityV3(entity)) return v3ToUnified(entity)

    if (isEntityV6(entity)) return v6ToUnified(entity)

    if (isEntityV7(entity)) return v7ToUnified(entity)

    throw assertUnreachable(entity, {
        surgeryBookingId: entity['surgeryBookingId'],
        bookingId: entity['bookingId'],
        hasSurgeryResources: isNotNullish(entity['surgeryResources']),
        hasResources: isNotNullish(entity['resources']),
    })
}

export function transformUnScheduledSurgeries(data: ToArray<IncomingEntity>): UnifiedUnScheduledSurgery[] {
    return data.map(transform).filter(isUnifiedUnScheduledSurgeryWithBookingId)
}
